// Blueprint: crap/blueprint/host-navigation/architecture/001-route-navigate-linkout.md
import { NavigateTargetType } from '@servicetitan/contactcenter-agent-inbox-mfe-loader';
import type { NavigateTarget } from '@servicetitan/contactcenter-agent-inbox-mfe-loader';
import { resolveTenantId } from '@modules/AuthModule/lib/resolveOrgNodeId';
import type { IContactCenterTenant } from '@modules/AuthModule/lib/types';
import {
  bookJobLinkToMonolith,
  chatCenterLinkToMonolith,
  customerLinkToMonolith,
  invoiceLinkToMonolith,
  jobBookingLinkToMonolith,
} from '@lib/utils/tenantLinks';

/**
 * Resolves the monolith URL a CustomerPanel/InboxMfe `NavigateTarget` link-out
 * should open, or `null` when the target's OrgNode Guid matches none of the
 * user's tenants (don't navigate to a broken link). Shared by every host
 * (InboxTextsPage, CustomerMfeAdapter, ...) wiring `onNavigate` — keeps the
 * NavigateTargetType switch + tenantId bridge in one place instead of a
 * per-host copy.
 *
 * Deliberately its own file rather than living in tenantLinks.ts: tenantLinks.ts
 * is imported across many unrelated routes (monitoring, reports, settings, ...),
 * and pulling in @servicetitan/contactcenter-agent-inbox-mfe-loader for
 * NavigateTarget/NavigateTargetType would make it a transitive dependency of
 * all of them. This file depends on tenantLinks.ts's URL builders, never the
 * other way around.
 */

/**
 * Stand-in for `NavigateTargetType.BookJobFallback` (CCPE-1635) until
 * `@servicetitan/contactcenter-agent-inbox-mfe-loader` publishes it — the member (and the
 * `NavigateTarget.locationId?`/`jobTypeId?` fields used below) landed in agent-inbox PR #484 but
 * haven't shipped past the pinned ^1.3.1 (currently 1.3.2). Delete this stub AND
 * CHAT_CENTER_TYPE AND ROUTE_TYPE in the same loader-bump PR — leaving one unpublished stand-in
 * is the next CCPE-1648-shaped hole. The loader's own inbox-mfe-loader.d.ts doc
 * comment warns this enum must stay in lockstep with its source of truth in
 * customer-panel, so this is strictly temporary.
 *
 * Exported so CustomerMfeAdapter.test.tsx/InboxTextsPage.test.tsx can construct a BookJobFallback
 * target without hardcoding a second copy of the string that could drift from this one.
 */
export const BOOK_JOB_FALLBACK_TYPE =
  'bookJobFallback' as unknown as NavigateTargetType;

/**
 * Stand-in for `NavigateTargetType.ChatCenter` (CCPE-216) until the loader
 * publishes it. Same reason as BOOK_JOB_FALLBACK_TYPE: pinned ^1.3.1 has no
 * member (or `contactNumber`). Delete this stub together with
 * BOOK_JOB_FALLBACK_TYPE in the same loader-bump PR, then read
 * `target.contactNumber` off the published type.
 */
export const CHAT_CENTER_TYPE = 'chatCenter' as unknown as NavigateTargetType;

/**
 * Stand-in for `NavigateTargetType.Route` (CCPE-2270) until the loader
 * publishes it — the member (and the `NavigateTarget.route`/`tenantId` fields
 * read by `resolveRouteNavigateTarget` below) is emitted today by
 * `contactcenter-agent-inbox` ahead of a published type. Delete this stub
 * together with BOOK_JOB_FALLBACK_TYPE and CHAT_CENTER_TYPE in the same
 * loader-bump PR, then read `target.route`/`target.tenantId` off the
 * published type.
 */
export const ROUTE_TYPE = 'route' as unknown as NavigateTargetType;

/**
 * Resolves a `Route`-typed `NavigateTarget` to the `{ tenantId, path }` pair the
 * `tenantRedirectUrl` BFF query needs — pure, synchronous, and DOM-free (no
 * `window.open`, no query issuance; that orchestration lives in
 * `openNavigateTarget.ts`). Returns `null` when the target has no usable route
 * or tenantId, per REQ-NAV-ROUTE-003 AC-1 / REQ-NAV-ROUTE-004 AC-2 — never a
 * fallback to the caller's own session tenant.
 *
 * The route is relayed **byte-identically** (REQ-NAV-ROUTE-001 AC-1): only
 * whitespace is trimmed and leading slashes are stripped defensively
 * (REQ-NAV-ROUTE-001 AC-2) — no character is ever percent-encoded, replaced,
 * or otherwise rewritten. The BFF composes `MonolithUrl + "/" + path`, so a
 * leading `/` on the route would otherwise double it.
 */
export function resolveRouteNavigateTarget(
  target: NavigateTarget,
): { tenantId: string; path: string } | null {
  const rawRoute = 'route' in target ? target.route : undefined;
  const trimmedRoute =
    typeof rawRoute === 'string' || typeof rawRoute === 'number'
      ? String(rawRoute).trim()
      : '';
  if (!trimmedRoute) {
    return null;
  }
  const path = trimmedRoute.replace(/^\/+/, '');
  if (!path) {
    return null;
  }

  const rawTenantId = 'tenantId' in target ? target.tenantId : undefined;
  const tenantId =
    typeof rawTenantId === 'string' || typeof rawTenantId === 'number'
      ? String(rawTenantId).trim()
      : '';
  if (!tenantId || tenantId === '0') {
    return null;
  }

  return { tenantId, path };
}

export function resolveNavigateUrl(
  target: NavigateTarget,
  tenants: ReadonlyArray<IContactCenterTenant>,
): string | null {
  // The monolith tenant-redirect URL takes a numeric tenant id, not an
  // OrgNode Guid — bridge back via the user's tenant list.
  const tenantId = resolveTenantId(
    tenants,
    'orgNodeId' in target ? target.orgNodeId : undefined,
  );
  if (!tenantId) {
    return null;
  }
  switch (target.type) {
    case NavigateTargetType.Job:
      return target.jobId
        ? jobBookingLinkToMonolith(tenantId, target.jobId)
        : customerLinkToMonolith(tenantId, String(target.customerId));
    case NavigateTargetType.Invoice:
      return target.invoiceId
        ? invoiceLinkToMonolith(tenantId, target.invoiceId)
        : customerLinkToMonolith(tenantId, String(target.customerId));
    /*
     * The panel's fallback intent carries optional locationId/jobTypeId to pre-select (set for
     * BookJobFallback), but the monolith's #/Calls screen has no query-param prefill mechanism for
     * either today — its job-type/location seeding only happens via an in-memory JS call from
     * inside an already-loaded SPA session (CallBoardModels.js's setScreenFromData), never from a
     * freshly-opened URL/tab. So this opens the same bare #/Calls screen as the existing "Book Job"
     * buttons (CustomerDetails, SpeedToLeadTaskView) — the fields are accepted on the contract
     * without a way to honor them yet, not silently dropped data the monolith could have used.
     */
    case BOOK_JOB_FALLBACK_TYPE:
      return bookJobLinkToMonolith(tenantId);
    /*
     * CCPE-216: Texts-rail Book Job is phone-keyed (`#/ChatCenter/{Normalize(phone)}`).
     * Ignore customerId even when present — using it is how this intent used to
     * open the CRM record. contactNumber is not on the published NavigateTarget
     * yet; guard the runtime field until the loader bump.
     */
    case CHAT_CENTER_TYPE: {
      const raw = 'contactNumber' in target ? target.contactNumber : undefined;
      const contactNumber =
        typeof raw === 'string' || typeof raw === 'number'
          ? String(raw).trim()
          : '';
      if (!contactNumber) {
        return null;
      }
      return chatCenterLinkToMonolith(tenantId, contactNumber);
    }
    /*
     * The record-level kinds above resolve to their own monolith route. The
     * section-level kinds below intentionally share the customer page: the monolith
     * has exactly one customer route (`/customer/:customerId` — see its
     * `customer-module-router.tsx`), and that page renders every section as one
     * long scroll with no per-section route, tab parameter, or anchor. There is no
     * more specific URL to send these to, so the customer record IS the correct
     * destination — not a lossy fallback. If the monolith ever adds deep-linkable
     * sections, these gain their own cases.
     */
    case NavigateTargetType.Customer:
    case NavigateTargetType.CustomerJobs:
    case NavigateTargetType.CustomerEquipment:
    case NavigateTargetType.CustomerBilling:
    case NavigateTargetType.CustomerLocations:
      return customerLinkToMonolith(tenantId, String(target.customerId));
    /*
     * An unrecognized type must never fall through to the customer page — a
     * Route target carries no customerId, so a host that doesn't yet handle
     * ROUTE_TYPE (openNavigateTarget branches on it before this switch is ever
     * reached) would otherwise silently open `#/customer/undefined`. No
     * compile-time exhaustiveness check is possible here (three of the handled
     * members above are `as unknown as NavigateTargetType` stub casts), so this
     * runtime `null` is the guard (REQ-NAV-ROUTE-002).
     */
    default:
      return null;
  }
}
